import { hideLoading, LoadableDataState, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import React, { Component } from "react";
import { TenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import FormSection from "@my-dld/components/Form/FormSection";
import Strings from "@my-dld/assets/strings";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import FormRow from "@eres/forms-web/FormRow";
import { TenantDocumentLookup } from "@eres/ejari-api/Entity";
import { EjariApi } from "@eres/ejari-api";
import Dropzone from "react-dropzone";
import DocumentInputField from "@my-dld/components/Form/DocumentInputField";
import { LocaleUtil } from "@eres/core";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { ErrorContainer } from "@eres/forms";
import { AttachmentInfo, TCDocumentInfo, TenancyContractResponse } from "@eres/ejari-api/Entity/tenant";
import TCDocument from "./TCDocument";
import { navigate } from "@my-dld/utils/StoreUtils";
import { TenancyContactAction, TenantDocumentType } from "@eres/ejari-core/constants";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { RenderActions } from "./ContractInfo";
import { updateTenantContext } from "@eres/ejari-core/contexts/TenantContext";
import FormMandatory from "../FormMandatory";

interface Props extends RoutedViewProps {

}

interface State extends LoadableDataState {
	documentInfo: TCDocumentInfo;
}

class DocumentInfo extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			documentInfo: {},
			errorContainer: {},
			loadState: "loading",
		};
	}

	componentDidMount(): void {
		const { documentInfo } = this.context;
		if ( documentInfo ) {
			this.setState( {
				documentInfo: documentInfo,
			} );
		} else {
			this.loadTenantAttachments();
		}
	}

	render() {
		const { action } = this.context;
		const { documentInfo, errorContainer } = this.state;
		const documents = documentInfo.Documents;
		return <div className="tender-registration-form">
			<FormSection title={ Strings.documents }>
				<FormMandatory/>
				{ documents && documents.map( ( docLookup: TenantDocumentLookup ) => {
					const isDocsAdded = ( docLookup && docLookup.Docs !== undefined && docLookup.Docs.length > 0 );
					const maxPages = docLookup.MaxPages ? docLookup.MaxPages : 1;
					const maxSizeKb = docLookup.MaxSizeKb ? ( docLookup.MaxSizeKb * 1000 ) : ( 2 * 1024 * 1000 );
					return <div key={ `doc_info_${ docLookup.Id }` } className="col-md-12 form-group">
						<Dropzone
							multiple={ maxPages > 1 }
							maxSize={ maxSizeKb }
							onDropRejected={ this.onDropRejected }
							onDrop={ ( files: File[] ) => {
								this.onDrop( docLookup, files );
							} }
							accept={ [ "image/png", "image/jpeg", "application/pdf" ] }>
							{ ( { getRootProps, getInputProps } ) => (
								<DocumentInputField { ...getRootProps() }
								                    required={ true }
								                    dropZoneInputProps={ getInputProps() }
								                    label={ LocaleUtil.getLocalValue( docLookup ) }
								                    isDocsAdded={ isDocsAdded }
								                    error={ errorContainer[ docLookup.Id ] }
								                    disabled={ ( docLookup.Docs && docLookup.Docs.length === maxPages ) }/>
							) }
						</Dropzone>
						<span>
                                <small>{ Strings.tabu_upload_documents_note }</small><br/>
                                <small>{ Strings.max_num_files + " : " + docLookup.MaxPages }</small>
                            </span>
						{ docLookup.Docs && docLookup.Docs.map( ( doc: AttachmentInfo, index: number ) => {
							return <TCDocument
								key={ `tenant_doc_${ index }` }
								index={ index }
								document={ doc }
								onDelete={ this.onDelete }
								onDocNameChange={ this.onDocNameChange }/>;
						} ) }
					</div>;
				} ) }
			</FormSection>
			<FormRow>
				<RenderActions
					action={ action! } onContinue={ this.onContinue }
					onSave={ this.onSave } onBack={ this.onBack }
					pLabel={ action === TenancyContactAction.TERMINATE_CONTRACT ? Strings.submit : Strings.continue }/>
			</FormRow>
		</div>;
	}

	private loadTenantAttachments() {
		showLoading();
		const action = this.context.action;
		const reqTypeId = ( action === TenancyContactAction.TERMINATE_CONTRACT ) ? TenantDocumentType.TERMINATE :
			TenantDocumentType.PERSON;
		EjariApi.getTenantContractDocuments( reqTypeId ).then( ( resp: TenantDocumentLookup[] ) => {
			hideLoading();
			this.setState( {
				documentInfo: {
					...this.state.documentInfo, Documents: resp
				}
			} );
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	}

	private onDropRejected = () => {
		showErrorMessage( Strings.tabu_file_exceed_size );
	};

	private onDrop = ( selDoc: TenantDocumentLookup, files: File[] ) => {
		if ( selDoc.MaxPages && selDoc.MaxPages > 1 ) {
			if ( selDoc.Docs && selDoc.Docs.length > 0 ) {
				let maxAllowed = ( selDoc.MaxPages - selDoc.Docs.length );
				if ( files.length > maxAllowed )
					files = files.splice( 0, maxAllowed );
			} else if ( files.length > selDoc.MaxPages ) {
				files = files.splice( 0, selDoc.MaxPages );
			}
		}
		files.filter( x => x.type.startsWith( "image/" ) || x.type.startsWith( "application/pdf" ) )
		.forEach( file => this.addFile( selDoc, file ) );
	};

	private addFile( selDoc: TenantDocumentLookup, file: File ) {
		const reader = new FileReader();
		reader.onload = () => {
			const binaryStr = reader.result;
			if ( selDoc.Docs == undefined )
				selDoc.Docs = [];
			const newDoc: AttachmentInfo = {
				DocumentID: selDoc.Id,
				Type: file.type,
				LocalData: binaryStr as string,
				FileName: undefined,
			};
			if ( selDoc.MaxPages && selDoc.MaxPages > 1 ) {
				selDoc.Docs.push( newDoc );
			} else {
				selDoc.Docs = [ newDoc ];
			}
			const info = this.state.documentInfo;
			let error = this.state.errorContainer;
			delete error[ selDoc.Id ];
			this.setState( {
				documentInfo: {
					...info,
					Documents: info.Documents!.map( ( doc: TenantDocumentLookup ) => {
						return ( selDoc.Id === doc.Id ) ? selDoc : doc;
					} )
				},
				errorContainer: error,
			} );
		};
		reader.readAsDataURL( file );
	}

	private onDelete = ( selDoc: AttachmentInfo, index: number ) => {
		if ( selDoc.Identity ) {
			// Deleting document in case of edit from request details
			showLoading();
			EjariApi.deleteTenancyContractDocument( selDoc.Identity ).then( _resp => {
				hideLoading();
				this.updateDeleteDocs( selDoc, index );
			} ).catch( e => {
				hideLoading();
				showErrorMessage( e.toString() );
			} );
		} else {
			this.updateDeleteDocs( selDoc, index );
		}
	};

	private updateDeleteDocs( selDoc: AttachmentInfo, index: number ) {
		const info = this.state.documentInfo;
		this.setState( {
			documentInfo: {
				...info,
				Documents: info.Documents!.map( ( doc: TenantDocumentLookup ) => {
					if ( selDoc.DocumentID === doc.Id ) {
						doc.Docs!.splice( index, 1 );
						return doc;
					}
					return doc;
				} )
			},
		}, () => {
			this.updateContractAttachments();
		} );
	}

	private updateContractAttachments() {
		// Updating Deleting documents in request details contract
		let { contract, action } = this.context;
		if ( contract && action === TenancyContactAction.UPDATE_CONTRACT ) {
			const { documentInfo } = this.state;
			let attachments: AttachmentInfo[] = [];
			documentInfo.Documents!.map( ( doc: TenantDocumentLookup ) => {
				attachments = attachments.concat( doc.Docs ? doc.Docs : [] );
			} );
			contract = {
				...contract,
				Attachments: attachments,
			};
			updateTenantContractContext( { contract: contract } );
		}
	}

	private onDocNameChange = ( selDoc: AttachmentInfo, index: number, name: string ) => {
		const info = this.state.documentInfo;
		this.setState( {
			documentInfo: {
				...info,
				Documents: info.Documents!.map( ( doc: TenantDocumentLookup ) => {
					if ( selDoc.DocumentID === doc.Id ) {
						const tDoc = doc.Docs![ index ];
						tDoc.FileName = name;
						tDoc.DocumentError = undefined;
						return doc;
					}
					return doc;
				} ),
			},
		} );
	};

	private isValid() {
		let isValid = true;
		const info = this.state.documentInfo;
		const error: ErrorContainer = {};
		let documents = info.Documents;
		if ( documents ) {
			documents.map( ( doc: TenantDocumentLookup ) => {
				if ( doc.IsMandatory === 1 && ( doc.Docs == undefined || doc.Docs.length === 0 ) ) {
					error[ doc.Id ] = Strings.required;
					isValid = false;
				}
				if ( doc.Docs && doc.Docs.length > 0 ) {
					doc.Docs.map( ( tDoc: AttachmentInfo ) => {
						if ( isEmptyStringOrNull( tDoc.FileName ) ) {
							isValid = false;
							tDoc.DocumentError = Strings.required;
						}
					} );
				}
			} );
		}
		this.setState( {
			documentInfo: { ...info, Documents: documents },
			errorContainer: error,
		} );
		return isValid;
	}

	private onContinue = () => {
		if ( this.isValid() ) {
			const action = this.context.action;
			updateTenantContractContext( {
				documentInfo: this.state.documentInfo,
			} );
			if ( action === TenancyContactAction.TERMINATE_CONTRACT ) {
				requestAnimationFrame( () => {
					this.terminateContract();
				} );
			} else {
				navigate( "remarks" );
			}
		}
	};

	private onSave = () => {
		if ( this.isValid() ) {
			let { contract } = this.context;
			const { documentInfo } = this.state;
			let attachments: AttachmentInfo[] = [];
			documentInfo.Documents!.map( ( doc: TenantDocumentLookup ) => {
				attachments = attachments.concat( doc.Docs! );
			} );
			contract = {
				...contract,
				Attachments: attachments,
			};
			updateTenantContractContext( { contract: contract, documentInfo: documentInfo, canResubmit: true } );
			this.onBack();
		}
	};

	private onBack = () => {
		this.props.history.goBack();
	};

	private terminateContract() {
		showLoading();
		const contractId = this.context.contract?.TenancyContractId + "";
		trackEventWithData( "TenantContract", "TerminateTry",
			{ contractId: contractId } );
		this.context.terminateContract( this.context ).then( ( _resp: TenancyContractResponse ) => {
			trackEventWithData( "TenantContract", "TerminateSubmitted", { contractId: contractId } );
			hideLoading();
			this.navigateToSuccess();
		} ).catch( e => {
			trackEventWithData( "TenantContract", "TerminateFailed", { contractId: contractId } );
			hideLoading();
			showErrorMessage( e );
		} );
	}

	private navigateToSuccess() {
		updateTenantContext( { actionDone: true, } );
		updateTenantContractContext( { actionDone: true, } );
		this.props.history.go( -2 );
	}
}

export default DocumentInfo;
